import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Wallet } from '@asia/shared';
import { useNav } from '../../store/nav';
import { useWallets } from '../../store/wallets';
import { haptic } from '../../hooks/useTelegram';
import { FigIcon } from '../FigIcon';
import { WalletAddressRow } from './WalletAddressRow';
import { WalletDetailSheet } from './WalletDetailSheet';
import { RenameWalletDialog } from './RenameWalletDialog';
import { DeleteWalletDialog } from './DeleteWalletDialog';

/** Dedicated wallets list (Profile → Manage wallets). Keeps the Profile screen
 *  short when there are many wallets: the full list + per-wallet management
 *  (detail / rename / delete / view seed) lives here instead. */
export function WalletsListScreen() {
  const { t } = useTranslation();
  const push = useNav((s) => s.push);
  const wallets = useWallets((s) => s.wallets);
  const renameWallet = useWallets((s) => s.rename);
  const removeWallet = useWallets((s) => s.remove);

  const [detail, setDetail] = useState<Wallet | null>(null);
  const [renaming, setRenaming] = useState<Wallet | null>(null);
  const [deleting, setDeleting] = useState<Wallet | null>(null);

  return (
    <div className="push-enter flex min-h-full flex-col bg-surface px-4 pb-40 pt-3">
      <header className="flex items-center py-2">
        <h1 className="text-[20px] font-medium text-ink">{t('wallet.myWallets')}</h1>
      </header>

      <div className="mt-2 flex w-full flex-col gap-4 rounded-[24px] bg-card p-4">
        {wallets.map((w, i) => (
          <div key={w.id} className="flex flex-col gap-4">
            {i > 0 && <div className="ml-[46px] h-px bg-hairline" />}
            <WalletAddressRow wallet={w} onClick={() => setDetail(w)} />
          </div>
        ))}
        <div className="ml-[46px] h-px bg-hairline" />
        <button
          onClick={() => {
            haptic();
            push({ name: 'wallets' });
          }}
          className="flex items-center gap-[6px] active:opacity-70"
        >
          <FigIcon name="add" size={20} className="text-accent" />
          <span className="text-[14px] text-accent">{t('wallet.createWallet')}</span>
        </button>
      </div>

      {detail && (
        <WalletDetailSheet
          wallet={detail}
          onClose={() => setDetail(null)}
          onSeed={() => {
            setDetail(null);
            push({ name: 'seed', walletId: detail.id });
          }}
          onRename={() => {
            setRenaming(detail);
            setDetail(null);
          }}
          onDelete={() => {
            setDeleting(detail);
            setDetail(null);
          }}
        />
      )}

      {renaming && (
        <RenameWalletDialog
          wallet={renaming}
          onConfirm={(name) => {
            renameWallet(renaming.id, name);
            setRenaming(null);
          }}
          onCancel={() => setRenaming(null)}
        />
      )}

      {deleting && (
        <DeleteWalletDialog
          wallet={deleting}
          onConfirm={() => {
            removeWallet(deleting.id);
            setDeleting(null);
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
